// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { classification6Classes } from './constants';

export interface PointCloudClass {
  name: string;
  color: string;
  classId: number;
}

// ASPRS LAS 1.4 standard classes. Codes 8 and 12 are reserved (handled via class
// flags, not class codes) so they have no named entry and fall through to the
// generated defaults below.
export const namedPointCloudClasses: PointCloudClass[] = [
  { name: 'Never Classified', color: '#555555', classId: 0 },
  { name: 'Unclassified', color: '#9b9c9a', classId: 1 },
  { name: 'Ground', color: '#a87000', classId: 2 },
  { name: 'Low Vegetation', color: '#a3d977', classId: 3 },
  { name: 'Medium Vegetation', color: '#61b34f', classId: 4 },
  { name: 'High Vegetation', color: '#2d7031', classId: 5 },
  { name: 'Buildings', color: '#ff0000', classId: 6 },
  { name: 'Low Point (Noise)', color: '#e64ca6', classId: 7 },
  { name: 'Water', color: '#1f78b4', classId: 9 },
  { name: 'Rail', color: '#31688e', classId: 10 },
  { name: 'Road Surface', color: '#4d4d4d', classId: 11 },
  { name: 'Wire - Guard', color: '#1f9e89', classId: 13 },
  { name: 'Wires', color: '#000000', classId: 14 },
  { name: 'Pole and Telco Towers', color: '#489af7', classId: 15 },
  { name: 'Wire Connector', color: '#6ece58', classId: 16 },
  { name: 'Bridge Deck', color: '#b5de2b', classId: 17 },
  { name: 'High Noise', color: '#ff2ad4', classId: 18 },
  { name: 'Overhead Structure', color: '#ff8c00', classId: 19 },
  { name: 'Ignored Ground', color: '#8a6d3b', classId: 20 },
  { name: 'Snow', color: '#bfe3e6', classId: 21 },
  { name: 'Temporal Exclusion', color: '#9467bd', classId: 22 },
  // AirWorks custom classes — named overrides take precedence over the generated
  // defaults. Vegetation Buffer only exists when vegetation trim is ordered.
  { name: 'Vegetation Buffer', color: '#eed202', classId: 25 },
];

export const hslToHex = (h: number, s: number, l: number): string => {
  const sat = s / 100;
  const light = l / 100;
  const k = (n: number): number => (n + h / 30) % 12;
  const a = sat * Math.min(light, 1 - light);
  const f = (n: number): string => {
    const channel = light - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    const hex = Math.round(255 * channel).toString(16);
    return `0${hex}`.slice(-2);
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

// Golden-angle hue rotation gives maximum separation between consecutive class IDs;
// lightness cycles 38/50/62% so no generated color approaches white.
export const defaultClassColor = (classId: number): string => {
  const hue = (classId * 137.508) % 360;
  const lightness = 38 + (classId % 3) * 12;
  return hslToHex(hue, 70, lightness);
};

// Full palette over the entire LAS class ID space (0–255): named entries win,
// every other ID gets a deterministic generated color so any class renders
// with a defined, never-white color without further code changes.
export const buildClassPalette = (named: PointCloudClass[]): PointCloudClass[] => {
  const namedById = new Map<number, PointCloudClass>(named.map((entry) => [entry.classId, entry]));
  const palette: PointCloudClass[] = [];
  for (let id = 0; id <= 255; id += 1) {
    const namedEntry = namedById.get(id);
    if (namedEntry) {
      palette.push(namedEntry);
    } else {
      const name = id <= 63 ? `Reserved ${id}` : `Custom ${id}`;
      palette.push({ name, color: defaultClassColor(id), classId: id });
    }
  }
  return palette;
};

export const fullPointCloudPalette = buildClassPalette(namedPointCloudClasses);
export const legacyPointCloudPalette = buildClassPalette(classification6Classes);

const usesNewClassSet = (lasType: string, newClasses?: boolean): boolean => lasType === 'all_classes' && !!newClasses;

export const getPointCloudPalette = (lasType: string, newClasses?: boolean): PointCloudClass[] => (
  usesNewClassSet(lasType, newClasses) ? fullPointCloudPalette : legacyPointCloudPalette
);

export const getNamedPointCloudClasses = (lasType: string, newClasses?: boolean): PointCloudClass[] => (
  usesNewClassSet(lasType, newClasses) ? namedPointCloudClasses : classification6Classes
);

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const toValidClassIds = (ids: number[]): number[] | null => {
  const valid = Array.from(new Set(ids.filter((id) => id >= 0 && id <= 255))).sort((a, b) => a - b);
  return valid.length > 0 ? valid : null;
};

// Parses the I3S CLASS_CODE cached-statistics response (as returned by
// PointCloudLayer.queryCachedStatistics) into the sorted list of class IDs present
// in the point cloud. Handles both documented shapes — mostFrequentValues
// value/count pairs and histogram counts offset by the histogram minimum — bare or
// nested under a stats key. Returns null when nothing is parseable so callers can
// fall back to the named class list.
export const parsePresentClassIds = (response: unknown): number[] | null => {
  if (!isRecord(response)) return null;
  const stats = isRecord(response.stats) ? response.stats : response;

  const { mostFrequentValues } = stats;
  if (Array.isArray(mostFrequentValues) && mostFrequentValues.length > 0) {
    const ids: number[] = [];
    mostFrequentValues.forEach((entry) => {
      if (!isRecord(entry) || typeof entry.value !== 'number') return;
      if (entry.count !== undefined && !(typeof entry.count === 'number' && entry.count > 0)) return;
      ids.push(Math.round(entry.value));
    });
    return toValidClassIds(ids);
  }

  const { histogram } = stats;
  if (isRecord(histogram) && typeof histogram.minimum === 'number' && Array.isArray(histogram.counts)) {
    const minimum = Math.round(histogram.minimum);
    const ids: number[] = [];
    histogram.counts.forEach((count, index) => {
      if (typeof count === 'number' && count > 0) ids.push(minimum + index);
    });
    return toValidClassIds(ids);
  }

  return null;
};

export interface PointCloudLegend {
  classes: PointCloudClass[];
  isFallback: boolean;
}

// Legend rows for a point cloud file: the classes actually present when statistics
// are available, otherwise the file's full named list. Callers append their Unknown
// pseudo-class only in fallback mode.
export const getLegendClasses = (
  lasType: string,
  newClasses: boolean | undefined,
  presentClassIds: number[] | null | undefined,
): PointCloudLegend => {
  if (!presentClassIds || presentClassIds.length === 0) {
    return { classes: getNamedPointCloudClasses(lasType, newClasses), isFallback: true };
  }
  const paletteById = new Map<number, PointCloudClass>(
    getPointCloudPalette(lasType, newClasses).map((entry) => [entry.classId, entry]),
  );
  const classes = [...presentClassIds]
    .sort((a, b) => a - b)
    .map((id) => paletteById.get(id))
    .filter((entry): entry is PointCloudClass => entry !== undefined);
  if (classes.length === 0) {
    return { classes: getNamedPointCloudClasses(lasType, newClasses), isFallback: true };
  }
  return { classes, isFallback: false };
};
